import { Alert, Modal, Spinner, Table } from "react-bootstrap";
import { useState, useEffect } from 'react';
import { getAuthToken } from "../../utils/AuthUtils";
import { API } from 'aws-amplify';
import "./styles.scss";

const roleCodes = ["CEO", "CFO", "HR", "GC"];
const REVENUE = "revenue";
const MARKET_CAP = "marketCap";
const TOTAL_ASSETS = "totalAssets";

const SurveyRegressionPreviewModal = ({ show, onClose, selectedSurveyRows, selectedSurveyTemplateId, surveyYear, regressionType }) => {

    const [loading, setLoading] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [regressionData, setRegressionData] = useState({});

    const fetchSurveyRegressionPreviewValues = async () => {
        setLoading(true);
        setShowError(false);
        const companyIds = extractCompanyIds(selectedSurveyRows);
        const queryStringParameters = {
            templateId: selectedSurveyTemplateId,
            surveyYear,
            regressionType,
            companyIds
        }
        const authToken = await getAuthToken();
        API.get('AnnualCompensations', "/survey-preview/regression", {
            headers: {
                'Token': authToken
            },
            queryStringParameters
        }).then((response) => {
            setLoading(false);
            setRegressionData(response);
        }).catch((error) => {
            setLoading(false);
            if (error !== undefined && error.response !== undefined &&
                error.response.data !== undefined && error.response.data.message !== undefined) {
                setErrorMessage(error.response.data.message);
                setShowError(true);
            }
        });
    }

    const regressionDataTable = (regressionType) => {
        return (<div>
            <Table striped bordered hover size="sm">
                <thead className="table-light">
                    <tr>
                        <th style={{ width: '10%' }} className="fw-semibold small text-uppercase py-2"></th>
                        <th colSpan={5} className="fw-semibold small text-uppercase py-2">Base Salary</th>
                        <th colSpan={5} className="fw-semibold small text-uppercase py-2">Target Cash</th>
                        <th colSpan={5} className="fw-semibold small text-uppercase py-2">Target TDC</th>
                    </tr>
                    <tr>
                        <th style={{ width: '10%' }} className="fw-semibold small text-uppercase py-2">Role</th>
                        <th style={{ width: '6%' }} className="fw-semibold small text-uppercase py-2">Data Points</th>
                        <th style={{ width: '6%' }} className="fw-semibold small text-uppercase py-2">Intercept</th>
                        <th style={{ width: '6%' }} className="fw-semibold small text-uppercase py-2">Slope</th>
                        <th style={{ width: '6%' }} className="fw-semibold small text-uppercase py-2">R Square</th>
                        <th style={{ width: '6%' }} className="fw-semibold small text-uppercase py-2">Std Err</th>
                        <th style={{ width: '6%' }} className="fw-semibold small text-uppercase py-2">Data Points</th>
                        <th style={{ width: '6%' }} className="fw-semibold small text-uppercase py-2">Intercept</th>
                        <th style={{ width: '6%' }} className="fw-semibold small text-uppercase py-2">Slope</th>
                        <th style={{ width: '6%' }} className="fw-semibold small text-uppercase py-2">R Square</th>
                        <th style={{ width: '6%' }} className="fw-semibold small text-uppercase py-2">Std Err</th>
                        <th style={{ width: '6%' }} className="fw-semibold small text-uppercase py-2">Data Points</th>
                        <th style={{ width: '6%' }} className="fw-semibold small text-uppercase py-2">Intercept</th>
                        <th style={{ width: '6%' }} className="fw-semibold small text-uppercase py-2">Slope</th>
                        <th style={{ width: '6%' }} className="fw-semibold small text-uppercase py-2">R Square</th>
                        <th style={{ width: '6%' }} className="fw-semibold small text-uppercase py-2">Std Err</th>
                    </tr>
                </thead>
                <tbody>
                    {roleCodes.map((role) => (
                        <tr key={role}>
                            <td>{role}</td>
                            <td>{regressionData[role]?.[regressionType]?.salary?.dataPoints}</td>
                            <td>{regressionData[role]?.[regressionType]?.salary?.intercept}</td>
                            <td>{regressionData[role]?.[regressionType]?.salary?.slope}</td>
                            <td>{regressionData[role]?.[regressionType]?.salary?.rSquared}</td>
                            <td>{regressionData[role]?.[regressionType]?.salary?.standardError}</td>
                            <td>{regressionData[role]?.[regressionType]?.targetCash?.dataPoints}</td>
                            <td>{regressionData[role]?.[regressionType]?.targetCash?.intercept}</td>
                            <td>{regressionData[role]?.[regressionType]?.targetCash?.slope}</td>
                            <td>{regressionData[role]?.[regressionType]?.targetCash?.rSquared}</td>
                            <td>{regressionData[role]?.[regressionType]?.targetCash?.standardError}</td>
                            <td>{regressionData[role]?.[regressionType]?.targetTDC?.dataPoints}</td>
                            <td>{regressionData[role]?.[regressionType]?.targetTDC?.intercept}</td>
                            <td>{regressionData[role]?.[regressionType]?.targetTDC?.slope}</td>
                            <td>{regressionData[role]?.[regressionType]?.targetTDC?.rSquared}</td>
                            <td>{regressionData[role]?.[regressionType]?.targetTDC?.standardError}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>)
    }

    const extractCompanyIds = (selectedSurveyRows) => {
        const companyIds = [];
        for (const row of selectedSurveyRows) {
            const elements = row.split("#");
            companyIds.push(elements[2]);
        }
        return companyIds.join(",");
    }

    const handleClose = () => {
        setLoading(false);
        setShowError(false);
        setErrorMessage("");
        setRegressionData({});
        onClose();
    }

    useEffect(() => {
        let isMounted = true;
        if (isMounted) {
            if (show) {
                fetchSurveyRegressionPreviewValues();
            }
        }
        return () => { isMounted = false };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    return (
        <Modal show={show} onHide={handleClose} size="xl" contentClassName="modal-container" dialogClassName="dashboard-modal">
            <Modal.Header closeButton>
                <h5 className='mb-0'>Regression Preview</h5>
            </Modal.Header>
            <Modal.Body>
                {(loading && regressionData) ? <Spinner
                    as="span"
                    animation="grow"
                    size="sm"
                    aria-hidden="true"
                /> :
                    <div>{regressionType === REVENUE && <><h6 className='mb-0'>Revenue</h6>{regressionDataTable(REVENUE)}</>}
                        {regressionType === MARKET_CAP && <><h6>Market Cap</h6>{regressionDataTable(MARKET_CAP)}</>}
                        {regressionType === TOTAL_ASSETS && <><h6>Total Assets</h6>{regressionDataTable(TOTAL_ASSETS)}</>}
                    </div>}
                {showError ?? <Alert variant="danger" dismissible>{errorMessage}</Alert>}
            </Modal.Body>
        </Modal>
    )
}

export default SurveyRegressionPreviewModal;
